import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {QUERY_KEYS} from '@/queryClient'
import {httpGenerateNrAbsences} from '@/features/accesses/http/accesses.http'
import {GenerateNrAbsencesError, GenerateNrAbsencesResponse} from '@/features/accesses/types'

/**
 * The generation plan is a read — a `dryRun` creates nothing — so it lives as a query, and it has to:
 * a mutation fired from a mount effect gets silently detached from its observer by StrictMode's
 * double mount (`MutationObserver.onUnsubscribe` drops the observer from the in-flight mutation and
 * nothing ever re-attaches it), freezing the dialog on `isPending` forever.
 *
 * Never cached: the plan is recomputed against live data every time the dialog opens.
 */
export const useGenerateNrAbsencesPreview = (
    params: {month: number; year: number},
    options?: Omit<
        UseQueryOptions<GenerateNrAbsencesResponse, AxiosError<GenerateNrAbsencesError>>,
        'queryKey' | 'queryFn'
    >
) =>
    useQuery<GenerateNrAbsencesResponse, AxiosError<GenerateNrAbsencesError>>({
        queryKey: [QUERY_KEYS.GENERATE_NR_ABSENCES_PREVIEW, params.month, params.year],
        queryFn: () => httpGenerateNrAbsences({month: params.month, year: params.year, dryRun: true}),
        staleTime: 0,
        gcTime: 0,
        // A locked period or a missing Amm user won't resolve on a second round trip.
        retry: false,
        ...options
    })
